<script setup>
import { nextTick, ref } from "vue";
import { FormTextarea } from "@Components/Form";
import { MagicIcon } from "@Icons/index";
import { useI18n } from "vue-i18n";
import Tooltip from "@Components/Tooltip.vue";
import { useForm } from "@/form";
import { LinkIcon } from "lucide-vue-next";
import { FormModal } from "@Components/Modal";
import { PrimaryButton } from "@Components/Button";

const message = defineModel("message");

defineProps({
    errors: {
        type: Array,
        default: [],
    },
});

const emit = defineEmits(["submit"]);

const { t: __ } = useI18n();

const focused = ref(false);
const processing = ref(false);
const sourceModal = ref(null);

const sources = ref("");

const submit = () => {
    if (message.value.trim() === "") return;

    emit("submit");
};

const rewritePrompt = async () => {
    if (message.value.trim().length === 0) return;
    if (processing.value) return;

    processing.value = true;

    const response = await useForm({
        prompt: message.value,
    }).post("one_click_rewrite_prompt");

    if (response.rewritten_prompt && response.rewritten_prompt.trim().length > 0) {
        message.value = response.rewritten_prompt;
    }

    processing.value = false;
};

const addSource = () => {
    message.value = __("Crée du contenu à partir de ces liens :") + "\n\n" + sources.value;
    sourceModal.value.hide();
    sources.value = "";
    nextTick(() => {
        submit();
    });
};
</script>
<template>
    <div
        class="relative rounded-md border-0 ring-1 ring-inset ring-gray-300 shadow-sm dark:ring-gray-800 dark:focus-within:ring-gray-700 focus-within:ring-2 focus-within:ring-inset focus-within:ring-gray-950">
        <textarea
            v-model="message"
            :placeholder="__('Comment otomatic peut vous aider aujourd\'hui ?')"
            class="w-full block px-3 py-2 text-sm leading-6 text-gray-900 bg-transparent border-none resize-none scroll-px-3 scroll-py-2 dark:text-white dark:placeholder:text-gray-400 focus:outline-0 focus:outline placeholder:text-gray-400"
            rows="3"
            @keydown.enter.exact.prevent="submit"></textarea>
        <div class="flex justify-between items-center gap-3 px-3 py-2">
            <div class="flex justify-between items-center gap-3">
                <Tooltip :content="__('Améliorer le prompt')" placement="top">
                    <MagicIcon
                        v-if="!processing"
                        class="size-5 text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900"
                        @click="rewritePrompt" />
                    <svg
                        v-else
                        class="flex-shrink-0 size-5 text-gray-900 animate-spin dark:text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </Tooltip>
                <Tooltip :content="__('Ajouter une source')" placement="top">
                    <LinkIcon
                        @click="$refs['sourceModal'].show()"
                        class="size-5 text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
                </Tooltip>
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-400">{{ __("Entrer pour envoyer") }}</div>
        </div>
        <FormModal ref="sourceModal" :title="__('Ajouter une source')" @submit="addSource">
            <template #body>
                <FormTextarea v-model="sources" :label="__('Ajouter des URLs, un sitemap ou un flux RSS')" rows="5" :z="1" />
            </template>
            <template #actions>
                <div class="flex justify-end items-center">
                    <PrimaryButton type="submit" :z="1">
                        {{ __("Envoyer") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>
    </div>
</template>
